import { API_ENDPOINTS } from '@/utils/constants/end-points';

import apiClient from '../api-client';

/* -------------------------------------------------------------------------- */
/* Rounding Policy API contract                                               */
/*                                                                            */
/* Every endpoint lives under /AwardManagement/ManageRoundingPolicy and       */
/* answers with the standard `{ success, data, message }` envelope, so each   */
/* call below unwraps `data` and returns the payload itself.                  */
/* -------------------------------------------------------------------------- */

/** The envelope every rounding-policy endpoint replies with. */
export interface RoundingPolicyResponse<T> {
  success: boolean;
  data: T;
  message: string;
}

/** A `{ id, name }` lookup entry backing one of the policy dropdowns. */
export interface PolicyLookupOption {
  id: number;
  name: string;
}

/* -------------------------------------------------------------------------- */
/* Master data                                                                */
/* -------------------------------------------------------------------------- */

/**
 * A sub-stage, scoped to the stage it belongs to.
 *
 * `subStageID` repeats across stages (Award Share is 5 under Contigent, Vested
 * *and* Final Stage), so a sub-stage is only meaningful alongside its
 * `stageID` — never look one up by id alone.
 */
export interface PolicySubStageMapping {
  subStageID: number;
  subStageName: string;
  subStageDescription: string;
  stageID: number;
}

/** A consumer sub-type (report / dashboard), scoped to its consumer type. */
export interface PolicyConsumerSubTypeMapping {
  subTypeID: number;
  subTypeName: string;
  subTypeDescription: string;
  typeID: number;
}

/**
 * A presentation field, scoped to the consumer sub-type that exposes it.
 *
 * `fieldID` is unique across the whole list, so a stored field resolves to its
 * label by id alone — but the *selectable* set is per consumer sub-type.
 */
export interface PolicyConsumerFieldMapping {
  fieldID: number;
  fieldName: string;
  fieldDescription: string;
  consumerSubTypeID: number;
}

/**
 * The master-data bundle backing the policy wizard's dropdowns.
 *
 * Keys mirror the wire exactly, including its inconsistent casing
 * (`subStageID` vs `stageID`), so renaming any of them would silently break
 * parsing. Every list is required on the backend's
 * `RoundingConfigurationDetailModel`, but they are optional here because the
 * slice merges the response onto an empty shape.
 */
export interface RoundingPolicyMasters {
  /** Half_Up, Half_Down, Floor, Ceiling, Truncate. */
  roundingTypes: PolicyLookupOption[];
  /** NoofShare, Addition Share, Unit Price, ... — the business field levels. */
  fieldLevels: PolicyLookupOption[];
  /** Contigent / Vested / Final / Crediting Stage. */
  stageLevels: PolicyLookupOption[];
  /** Sub-stages, filtered by the rule's stage. */
  subStageLevels: PolicySubStageMapping[];
  /** DashBoard, Report. */
  consumerTypes: PolicyLookupOption[];
  /** Consumer sub-types, filtered by the scope's consumer type. */
  consumerSubTypes: PolicyConsumerSubTypeMapping[];
  /** First Row, Last Row, Largest Row. */
  reconcilStrategyTypes: PolicyLookupOption[];
  /** Presentation fields, filtered by the scope's consumer sub-type. */
  consumerTypeFieldMappings: PolicyConsumerFieldMapping[];
}

/**
 * GET /AwardManagement/ManageRoundingPolicy — the policy lookup lists.
 *
 * Despite the resource path this returns master data rather than policies;
 * use `searchRoundingPolicies` for the policy list itself. The result is
 * `Partial` because a list the backend has no rows for comes back absent.
 */
export const fetchRoundingPolicyMasters = async (): Promise<
  Partial<RoundingPolicyMasters>
> => {
  const res = await apiClient.get<
    RoundingPolicyResponse<Partial<RoundingPolicyMasters>>
  >(API_ENDPOINTS.ROUNDINGPOLICY);
  return res?.data?.data ?? {};
};

/* -------------------------------------------------------------------------- */
/* Policy model                                                               */
/*                                                                            */
/* Responses carry an id *and* a name for every lookup; requests carry the id  */
/* only. The `*Name` fields are therefore optional on the payload types.       */
/*                                                                            */
/* The names are also unreliable: the staged-policy endpoint answers with them  */
/* blank, so the UI resolves every label from master data rather than trusting  */
/* the wire.                                                                   */
/* -------------------------------------------------------------------------- */

/** A field-level rule — rounding applied to a business field (NoofShare, ...). */
export interface RoundingPolicyFieldLevel {
  fieldLevelId: number;
  fieldLevelName: string;
  roundingTypeId: number;
  roundingTypeName: string;
  precision: number;
}

/**
 * A stage-level rule: rounding applied to one sub-stage of one stage.
 *
 * The backend requires both ids to be greater than zero, so a rule is never
 * scoped to a sub-stage alone.
 */
export interface RoundingPolicyStageLevel {
  stageLevelId: number;
  stageLevelName: string;
  subStageId: number;
  subStageName: string;
  roundingTypeId: number;
  roundingTypeName: string;
  precision: number;
}

export interface RoundingPolicyBusinessModel {
  businessFieldLevels: RoundingPolicyFieldLevel[];
  businessStageLevels: RoundingPolicyStageLevel[];
}

/** One rounding rule inside a presentation scope, keyed by presentation field. */
export interface RoundingPolicyPresentationSubType {
  presentationFieldID: number;
  presentationFieldName?: string | null;
  roundingId: number;
  roundingName: string;
  precision: number;
}

/**
 * One presentation scope: a consumer (DashBoard / Report) and its sub type
 * (User DashBoard, ShareCrediting Report, ...), holding that scope's rules.
 */
export interface RoundingPolicyPresentation {
  consumerTypeId: number;
  consumerTypeName: string;
  consumerTypeSubId: number;
  consumerTypeSubName: string;
  presentationSubTypes: RoundingPolicyPresentationSubType[];
}

/**
 * The reconciliation model.
 *
 * Every switch here is a plain boolean — `isRaw` replaced the old aggregate
 * lookup, and `isCash` replaced the cash-type lookup, so only the adjustment
 * strategy is still an id.
 */
export interface RoundingPolicyReconciliation {
  isRaw: boolean;
  adjustmentStrategyId: number;
  adjustmentStrategyName: string;
  /** Whole number 0–6; the backend rejects anything outside that range. */
  maxAdjustPrecision: number;
  isEquity: boolean;
  isCash: boolean;
  isReconRawValue: boolean;
  isReconAdjustValue: boolean;
  isReconDeltaValue: boolean;
}

/** A rounding policy as returned by /Search, /List and /txn/{txnId}. */
export interface RoundingPolicy {
  policyId: number;
  policyName: string;
  policyVersion: number;
  planTypeId: number;
  planTypeName: string;
  isEquityPlan: boolean;
  countryId: number;
  countryName: string;
  /** ISO 8601 timestamp, sent without a timezone suffix. */
  effectiveDate: string;
  policyStatusId: number;
  policyStatusName: string;
  createdBy: number;
  createdDate: string;
  lastUpdatedBy: number | null;
  lastUpdatedDate: string | null;
  policyBusinessModel: RoundingPolicyBusinessModel;
  policyPresentationModel: RoundingPolicyPresentation[];
  policyReconciliationModel: RoundingPolicyReconciliation;
  /** True while the policy is still a maker draft. */
  isDraft?: boolean;
  /** Maker-checker transaction id; set on staged (pending) policies. */
  txnId?: string | null;
  /**
   * Workflow status of a staged policy (e.g. "Pending Checker Approval"). The
   * staged-policy endpoint does not send it, so the pending-approvals row's
   * status is handed over on navigation instead.
   */
  txnStatus?: string | null;
}

/**
 * The create / update / revise body.
 *
 * The backend reads ids only — the `*Name` halves are accepted but ignored, so
 * every field is optional here and the wizard omits a pair it cannot resolve
 * from master data rather than sending a placeholder id.
 *
 * Two exceptions the backend marks required and will 400 on: `policyName`,
 * `policyVersion`, `planTypeId`, `countryId` and `effectiveDate` must be set,
 * and `policyStatusName` must be a non-empty string even though
 * `policyStatusId` may be left at 0 for the backend to default.
 */
export interface RoundingPolicyPayload extends Partial<
  Omit<
    RoundingPolicy,
    | 'policyBusinessModel'
    | 'policyPresentationModel'
    | 'policyReconciliationModel'
  >
> {
  policyBusinessModel?: {
    businessFieldLevels?: Partial<RoundingPolicyFieldLevel>[];
    businessStageLevels?: Partial<RoundingPolicyStageLevel>[];
  };
  policyPresentationModel?: (Partial<
    Omit<RoundingPolicyPresentation, 'presentationSubTypes'>
  > & {
    presentationSubTypes?: Partial<RoundingPolicyPresentationSubType>[];
  })[];
  policyReconciliationModel?: Partial<RoundingPolicyReconciliation>;
  isDraft?: boolean;
}

/**
 * The transaction id a save replies with.
 *
 * Create and revise answer `TransactionId`, update and delete
 * `transactionId` — the casing is per-endpoint on the backend, so read both.
 */
export const getRoundingPolicyTransactionId = (
  res?: RoundingPolicyResponse<Record<string, unknown>> | null
): string | undefined => {
  const data = res?.data;
  const id = data?.TransactionId ?? data?.transactionId;
  return typeof id === 'string' ? id : undefined;
};

/* -------------------------------------------------------------------------- */
/* Search                                                                     */
/* -------------------------------------------------------------------------- */

/** Caller-facing search options, in the codebase's usual camelCase. */
export interface RoundingPolicySearchParams {
  searchField?: string;
  /**
   * Zero-based *page index*, not a record count: the backend pages with
   * `Skip(Offset * Limit)`. Never pass `page * limit`.
   */
  offset?: number;
  limit?: number;
  isExport?: boolean;
  totalCount?: number;
  totalPages?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

/** The paging block echoed back alongside the matched policies. */
export interface RoundingPolicyPagination {
  offset: number;
  limit: number;
  isExport: boolean;
  totalCount: number;
  totalPages: number;
  sortBy: string | null;
  sortDir: string | null;
}

export interface RoundingPolicySearchResult {
  roundingPolicies: RoundingPolicy[];
  pagination?: RoundingPolicyPagination;
}

/** The endpoint namespaces its paging options under a `Pagination.` prefix. */
const PAGINATION_PREFIX = 'Pagination.';
const PAGINATION_WIRE_KEYS = {
  offset: 'Offset',
  limit: 'Limit',
  isExport: 'IsExport',
  totalCount: 'TotalCount',
  totalPages: 'TotalPages',
  sortBy: 'SortBy',
  sortDir: 'SortDir',
} as const;

/**
 * GET /AwardManagement/ManageRoundingPolicy/Search — the paged policy list.
 *
 * The endpoint takes its paging options as dotted query keys
 * (`Pagination.Offset`), so the camelCase options above are mapped onto the
 * wire names here. Unset options are dropped rather than sent empty.
 */
export const searchRoundingPolicies = async (
  params: RoundingPolicySearchParams = {}
): Promise<RoundingPolicySearchResult> => {
  const query: Record<string, unknown> = {};

  if (params.searchField !== undefined) {
    query.SearchField = params.searchField;
  }

  Object.entries(PAGINATION_WIRE_KEYS).forEach(([option, wireKey]) => {
    const value = params[option as keyof RoundingPolicySearchParams];
    if (value !== undefined) {
      query[`${PAGINATION_PREFIX}${wireKey}`] = value;
    }
  });

  const res = await apiClient.get<
    RoundingPolicyResponse<RoundingPolicySearchResult>
  >(`${API_ENDPOINTS.ROUNDINGPOLICY}/Search`, { params: query });

  return res?.data?.data ?? { roundingPolicies: [] };
};

/**
 * GET /AwardManagement/ManageRoundingPolicy/List — the approved policies, in
 * full.
 *
 * Unpaged and unfiltered: it backs the simulation picker, which needs each
 * policy's own rules to know which field and stage levels are worth offering.
 */
export const fetchRoundingPolicyList = async (): Promise<RoundingPolicy[]> => {
  const res = await apiClient.get<
    RoundingPolicyResponse<RoundingPolicySearchResult>
  >(`${API_ENDPOINTS.ROUNDINGPOLICY}/List`);
  return res?.data?.data?.roundingPolicies ?? [];
};

/* -------------------------------------------------------------------------- */
/* CRUD                                                                       */
/*                                                                            */
/* There is no GET /{policyId}: the resource exposes only PUT and DELETE on an  */
/* id, so a saved policy is read back through /Search or /txn/{txnId}.          */
/* -------------------------------------------------------------------------- */

/** POST /AwardManagement/ManageRoundingPolicy — create a rounding policy. */
export const createRoundingPolicy = async (payload: RoundingPolicyPayload) => {
  const res = await apiClient.post(API_ENDPOINTS.ROUNDINGPOLICY, payload);
  return res?.data;
};

/** PUT /AwardManagement/ManageRoundingPolicy/{policyId} — update a policy. */
export const updateRoundingPolicy = async (
  policyId: number | string,
  payload: RoundingPolicyPayload
) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS.ROUNDINGPOLICY}/${policyId}`,
    payload
  );
  return res?.data;
};

/** DELETE /AwardManagement/ManageRoundingPolicy/{policyId} — delete a policy. */
export const deleteRoundingPolicy = async (policyId: number | string) => {
  const res = await apiClient.delete(
    `${API_ENDPOINTS.ROUNDINGPOLICY}/${policyId}`
  );
  return res?.data;
};

/**
 * Create or update in one call, keyed on whether the policy already exists.
 *
 * There is no draft-specific endpoint: a work-in-progress policy is an ordinary
 * POST / PUT flagged `isDraft`, which parks the transaction with the maker
 * instead of sending it to the checker.
 */
export const saveRoundingPolicy = async (
  payload: RoundingPolicyPayload,
  policyId?: number | string
) =>
  policyId
    ? updateRoundingPolicy(policyId, { ...payload, policyId: Number(policyId) })
    : createRoundingPolicy(payload);

/* -------------------------------------------------------------------------- */
/* Simulation                                                                 */
/* -------------------------------------------------------------------------- */

/** One simulated rule application: the input value and what it rounds to. */
export interface RoundingPolicySimulationDetail {
  id: number;
  policyName: string;
  ruleName: string;
  /** Empty when the matched rule is field-level rather than stage-level. */
  stageLevelName: string;
  /** Empty when the matched rule is stage-level rather than field-level. */
  fieldLevelName: string;
  inputValue: number;
  roundedValue: number;
  /** The wire sends the applied precision as a string. */
  precisionApplied: string;
}

/**
 * Simulation filters. `stageLevelId` / `fieldLevelId` default to 0, which the
 * endpoint reads as "every level", returning one row per matching rule.
 */
export interface RoundingPolicySimulationParams {
  policyId: number | string;
  policyVersion: number | string;
  inputValue: number | string;
  stageLevelId?: number | string;
  fieldLevelId?: number | string;
  /**
   * The unsaved policy being created, simulated in place of a stored one.
   * Sent with policyId / policyVersion 0, since the policy has neither yet.
   */
  payload?: RoundingPolicyPayload;
}

/**
 * POST /AwardManagement/ManageRoundingPolicy/Simulation/Details — run one value
 * through a policy's rules.
 *
 * A POST despite being a read: the request is a body, not a query. A saved
 * policy is looked up by id + version and `payload` goes empty; a new policy
 * travels as the JSON-encoded create body in `payload`.
 */
export const fetchRoundingPolicySimulationDetails = async ({
  policyId,
  policyVersion,
  inputValue,
  stageLevelId = 0,
  fieldLevelId = 0,
  payload,
}: RoundingPolicySimulationParams): Promise<
  RoundingPolicySimulationDetail[]
> => {
  const res = await apiClient.post<
    RoundingPolicyResponse<RoundingPolicySimulationDetail[]>
  >(`${API_ENDPOINTS.ROUNDINGPOLICY}/Simulation/Details`, {
    policyId: Number(policyId),
    policyVersion: Number(policyVersion),
    stageLevelId: Number(stageLevelId),
    fieldLevelId: Number(fieldLevelId),
    inputValue: Number(inputValue),
    payload: payload ? JSON.stringify(payload) : '',
  });
  return res?.data?.data ?? [];
};

/* -------------------------------------------------------------------------- */
/* Maker-checker                                                              */
/* -------------------------------------------------------------------------- */

/** Checker decision codes accepted by the checker-action endpoint. */
export const ROUNDING_POLICY_CHECKER_ACTIONS = {
  APPROVE: '7',
  REJECT: '8',
  REVISION: '9',
} as const;

export type RoundingPolicyCheckerAction =
  (typeof ROUNDING_POLICY_CHECKER_ACTIONS)[keyof typeof ROUNDING_POLICY_CHECKER_ACTIONS];

/**
 * Filters for the pending-approvals list, in the codebase's camelCase.
 *
 * `IsChecker` and `CurrentUserId` are query options on the endpoint but the
 * controller overwrites both from the caller's token, so they are deliberately
 * not exposed here.
 */
export interface RoundingPolicyPendingApprovalsParams {
  /**
   * Zero-based page index, matching every other pending-approvals list.
   *
   * Note: this endpoint currently pages with `Skip(Offset)` rather than
   * `Skip(Offset * Limit)`, so pages beyond the first are wrong until the
   * backend is aligned with the other lists.
   */
  offset?: number;
  limit?: number;
  searchField?: string;
  sortDir?: 'asc' | 'desc';
  /**
   * Numeric workflow-status ids. Several ids are sent as one comma-separated
   * `WorkflowStatusId` value ("1,4"); omitted, every in-flight status returns.
   */
  workflowStatusId?: number | number[];
  /** Fetches the full list for download (backend ignores paging). */
  isExport?: boolean;
}

/**
 * One row of the pending-approvals list.
 *
 * A transaction summary rather than a policy: it carries the maker / checker
 * audit trail and the workflow status, but none of the rounding rules, and its
 * maker fields are display names rather than ids.
 */
export interface RoundingPolicyPendingApprovalRow {
  id: number;
  txnId: string;
  /** Null on a Create transaction — the policy does not exist yet. */
  policyId: number | null;
  policyName: string;
  policyVersion: number;
  countryName: string;
  effectiveDate: string;
  /** Create / Update / Delete. */
  workflowActionName?: string | null;
  txnStatus: string;
  /** Maker's display name, not an id — see `createdByUserId` for that. */
  createdBy: string;
  createdByUserId: number;
  createdAt: string;
  lastMaker?: string | null;
  lastMakerDatetime?: string | null;
  lastChecker?: string | null;
  lastCheckerDatetime?: string | null;
  makerRemarks?: string | null;
  checkerRemarks?: string | null;
}

export interface RoundingPolicyPendingApprovalsResult {
  items: RoundingPolicyPendingApprovalRow[];
  totalCount: number;
  offset: number;
  limit: number;
}

const PENDING_APPROVALS_WIRE_KEYS = {
  offset: 'Offset',
  limit: 'Limit',
  searchField: 'SearchField',
  sortDir: 'SortDir',
  workflowStatusId: 'WorkflowStatusId',
  isExport: 'IsExport',
} as const;

/**
 * GET /AwardManagement/ManageRoundingPolicy/pending-approvals — the maker /
 * checker list of staged policies (paged). Unset filters are dropped, not sent
 * empty.
 */
export const fetchPendingRoundingPolicyApprovals = async (
  params: RoundingPolicyPendingApprovalsParams = {}
): Promise<RoundingPolicyPendingApprovalsResult | undefined> => {
  const query: Record<string, unknown> = {};

  Object.entries(PENDING_APPROVALS_WIRE_KEYS).forEach(([option, wireKey]) => {
    const value = params[option as keyof RoundingPolicyPendingApprovalsParams];
    if (value !== undefined) {
      query[wireKey] = Array.isArray(value) ? value.join(',') : value;
    }
  });

  const res = await apiClient.get<
    RoundingPolicyResponse<RoundingPolicyPendingApprovalsResult>
  >(`${API_ENDPOINTS.ROUNDINGPOLICY}/pending-approvals`, { params: query });
  return res?.data?.data;
};

/**
 * GET /AwardManagement/ManageRoundingPolicy/txn/{txnId} — a staged policy by
 * its transaction id, for checker review or maker revision.
 *
 * The endpoint answers with the same paged wrapper as /Search even though a
 * transaction holds exactly one policy, so the single entry is unwrapped here.
 */
export const fetchRoundingPolicyByTxnId = async (
  txnId: string
): Promise<RoundingPolicy | undefined> => {
  const res = await apiClient.get<
    RoundingPolicyResponse<RoundingPolicySearchResult>
  >(`${API_ENDPOINTS.ROUNDINGPOLICY}/txn/${txnId}`);
  return res?.data?.data?.roundingPolicies?.[0];
};

/**
 * PUT /AwardManagement/ManageRoundingPolicy/revise/{txnId} — maker
 * resubmission of a staged policy. Same body as create/update.
 */
export const reviseRoundingPolicy = async (
  txnId: string,
  payload: RoundingPolicyPayload
) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS.ROUNDINGPOLICY}/revise/${txnId}`,
    { ...payload, txnId }
  );
  return res?.data;
};

/**
 * POST /AwardManagement/ManageRoundingPolicy/checker-action — approve, reject
 * or send back a staged policy. See ROUNDING_POLICY_CHECKER_ACTIONS for codes.
 */
export const roundingPolicyCheckerAction = async (payload: {
  txnId: string;
  action: RoundingPolicyCheckerAction;
  remarks?: string;
}) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS.ROUNDINGPOLICY}/checker-action`,
    payload
  );
  return res?.data;
};
